import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Flex,
  useToast,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { theme as mythboundTheme } from '../config/theme';
import { AuthenticationManager } from '../integration/AuthenticationManager';
import { RoleType, roles } from '../config/roles';
import RoleBasedAccess from '../components/auth/RoleBasedAccess';
import { apiConfig } from '../config/api';

interface User {
  id: string;
  username: string;
  email: string;
  roles: RoleType[];
  provider: string;
  lastLogin: string;
}

interface MonitoringExtras {
  request_rate?: number | null;
  error_rate?: number | null;
  campaigns?: number | null;
  sessions?: number | null;
}

interface MonitoringPayload {
  extras?: MonitoringExtras;
  voice_bot_health?: {
    active_sessions?: number | null;
  };
}

/**
 * Admin Dashboard Component
 * 
 * This component provides administrative functions including:
 * - User management (view users, assign roles)
 * - System stats and metrics
 * - Audit logs
 */
export const AdminDashboard: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedRole, setSelectedRole] = useState<RoleType>(RoleType.USER);
  const [monitoring, setMonitoring] = useState<MonitoringPayload | null>(null);
  const [monitoringError, setMonitoringError] = useState<string | null>(null);
  const [dbHealth, setDbHealth] = useState<{ status: 'ok' | 'error'; detail?: string } | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const authManager = new AuthenticationManager();
  const handleCopyDbDiagnostics = () => {
    if (!dbHealth || dbHealth.status !== 'error') return;
    const message = `Database unavailable. ${dbHealth.detail || 'Check SUPABASE_POSTGRES_URL on the backend.'}`;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(message).catch((error) => {
        console.error('[AdminDashboard] Failed to copy diagnostics:', error);
      });
    }
  };
  
  // Fetch users data
  useEffect(() => {
    // Initialize empty - will fetch real users from API
    setUsers([]);
  }, []);

  // Load monitoring data once for system stats
  useEffect(() => {
    const loadMonitoring = async () => {
      try {
        // Added 2026-02-24: Include Bearer token for backend HTTPBearer() auth
        const accessToken = localStorage.getItem('access_token');
        const response = await fetch(`${apiConfig.baseUrl}/admin/monitoring`, {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const payload = await response.json();
        setMonitoring(payload);
        setMonitoringError(null);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load monitoring data';
        setMonitoringError(message);
        console.error('[AdminDashboard] Monitoring load failed:', error);
      }
    };

    loadMonitoring();
  }, []);

  useEffect(() => {
    const loadDbHealth = async () => {
      try {
        // Added 2026-02-24: Include Bearer token for backend HTTPBearer() auth
        const accessToken = localStorage.getItem('access_token');
        const response = await fetch(`${apiConfig.baseUrl}/health/db`, {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
          },
        });

        const payload = await response.json();
        if (payload?.status === 'ok') {
          setDbHealth({ status: 'ok' });
        } else {
          setDbHealth({ status: 'error', detail: payload?.detail || 'Database unreachable' });
        }
      } catch (error) {
        setDbHealth({ status: 'error', detail: 'Database health check failed' });
        console.error('[AdminDashboard] DB health check failed:', error);
      }
    };

    loadDbHealth();
  }, []);
  
  // Handle user role changes
  const handleUserRoleChange = () => {
    if (!selectedUser) return;
    
    // In a real app, this would make an API call to update the user's role
    const updatedUsers = users.map(user => {
      if (user.id === selectedUser.id) {
        // If user already has this role, don't add it again
        if (!user.roles.includes(selectedRole)) {
          return {
            ...user,
            roles: [selectedRole, ...user.roles.filter(role => role !== selectedRole)]
          };
        }
      }
      return user;
    });
    
    setUsers(updatedUsers);
    onClose();
    
    toast({
      title: 'Role updated',
      description: `${selectedUser.username} has been assigned the role: ${roles[selectedRole].name}`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };
  
  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };
  
  // Open role change modal
  const openRoleModal = (user: User) => {
    setSelectedUser(user);
    setSelectedRole(user.roles[0] || RoleType.USER);
    onOpen();
  };
  
  return (
    <RoleBasedAccess requiredRoles={[RoleType.ADMIN]}>
      <Box p={5}>
        <Heading 
          fontFamily={mythboundTheme.typography.fontFamily.heading}
          color={mythboundTheme.colors.primary.gold}
          mb={6}
          size="xl"
        >
          Admin Dashboard
        </Heading>
        
        <Text color={mythboundTheme.colors.secondary.white} mb={6}>
          Welcome, {authManager.getUserInfo()?.username}! You have administrative access.
        </Text>
        
        <Tabs variant="enclosed" colorScheme="blue">
          <TabList>
            <Tab 
              _selected={{ 
                color: mythboundTheme.colors.primary.main,
                bg: 'rgba(59, 232, 255, 0.1)',
                borderColor: mythboundTheme.colors.primary.main
              }}
              color={mythboundTheme.colors.secondary.white}
            >
              User Management
            </Tab>
            <Tab
              _selected={{ 
                color: mythboundTheme.colors.primary.main,
                bg: 'rgba(59, 232, 255, 0.1)',
                borderColor: mythboundTheme.colors.primary.main
              }}
              color={mythboundTheme.colors.secondary.white}
            >
              System Stats
            </Tab>
            <Tab
              _selected={{ 
                color: mythboundTheme.colors.primary.main,
                bg: 'rgba(59, 232, 255, 0.1)',
                borderColor: mythboundTheme.colors.primary.main
              }}
              color={mythboundTheme.colors.secondary.white}
            >
              Audit Logs
            </Tab>
          </TabList>
          
          <TabPanels bg="rgba(17, 17, 22, 0.7)" borderRadius="md" mt={2}>
            <TabPanel>
              <Heading 
                size="md" 
                color={mythboundTheme.colors.secondary.white}
                mb={4}
              >
                User Management
              </Heading>
              
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th color={mythboundTheme.colors.accent.silver}>Username</Th>
                    <Th color={mythboundTheme.colors.accent.silver}>Email</Th>
                    <Th color={mythboundTheme.colors.accent.silver}>Provider</Th>
                    <Th color={mythboundTheme.colors.accent.silver}>Roles</Th>
                    <Th color={mythboundTheme.colors.accent.silver}>Last Login</Th>
                    <Th color={mythboundTheme.colors.accent.silver}>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {users.map(user => (
                    <Tr key={user.id}>
                      <Td color={mythboundTheme.colors.secondary.white}>{user.username}</Td>
                      <Td color={mythboundTheme.colors.secondary.white}>{user.email}</Td>
                      <Td color={mythboundTheme.colors.secondary.white}>
                        <Badge
                          colorScheme={user.provider === 'discord' ? 'purple' : 'red'}
                        >
                          {user.provider}
                        </Badge>
                      </Td>
                      <Td>
                        {user.roles.map(role => (
                          <Badge
                            key={role}
                            colorScheme={
                              role === RoleType.ADMIN ? 'red' :
                              role === RoleType.MODERATOR ? 'orange' :
                              role === RoleType.PREMIUM ? 'green' :
                              'gray'
                            }
                            mr={1}
                          >
                            {roles[role].name}
                          </Badge>
                        ))}
                      </Td>
                      <Td color={mythboundTheme.colors.secondary.white}>{formatDate(user.lastLogin)}</Td>
                      <Td>
                        <Button
                          size="sm"
                          onClick={() => openRoleModal(user)}
                          colorScheme="blue"
                          variant="outline"
                        >
                          Change Role
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TabPanel>
            
            <TabPanel>
              <Heading 
                size="md" 
                color={mythboundTheme.colors.secondary.white}
                mb={4}
              >
                System Statistics
              </Heading>

              {dbHealth?.status === 'error' && (
                <Alert status="error" mb={4} borderRadius="md">
                  <AlertIcon />
                  <Box flex="1">
                    <Text color={mythboundTheme.colors.secondary.white}>
                      Database unavailable. {dbHealth.detail || 'Check SUPABASE_POSTGRES_URL on the backend.'}
                    </Text>
                  </Box>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCopyDbDiagnostics}
                  >
                    Copy diagnostics
                  </Button>
                </Alert>
              )}
              
              <Flex flexWrap="wrap" gap={4}>
                <Box 
                  p={4} 
                  borderWidth="1px" 
                  borderRadius="md" 
                  flex="1" 
                  bg="rgba(59, 232, 255, 0.05)"
                  borderColor={mythboundTheme.colors.accent.teal}
                >
                  <Heading size="sm" color={mythboundTheme.colors.primary.main} mb={2}>
                    Total Users
                  </Heading>
                  <Text fontSize="2xl" color={mythboundTheme.colors.secondary.white}>
                    {users.length}
                  </Text>
                </Box>
                
                <Box 
                  p={4} 
                  borderWidth="1px" 
                  borderRadius="md" 
                  flex="1"
                  bg="rgba(59, 232, 255, 0.05)"
                  borderColor={mythboundTheme.colors.accent.teal}
                >
                  <Heading size="sm" color={mythboundTheme.colors.primary.main} mb={2}>
                    Active Sessions
                  </Heading>
                  <Text fontSize="2xl" color={mythboundTheme.colors.secondary.white}>
                    {monitoring?.voice_bot_health?.active_sessions ?? 0}
                  </Text>
                </Box>
                
                <Box 
                  p={4} 
                  borderWidth="1px" 
                  borderRadius="md" 
                  flex="1"
                  bg="rgba(59, 232, 255, 0.05)"
                  borderColor={mythboundTheme.colors.accent.teal}
                >
                  <Heading size="sm" color={mythboundTheme.colors.primary.main} mb={2}>
                    Request Rate (/min)
                  </Heading>
                  <Text fontSize="2xl" color={mythboundTheme.colors.secondary.white}>
                    {monitoring?.extras?.request_rate ?? '—'}
                  </Text>
                  {monitoringError && (
                    <Text fontSize="xs" color="orange.300" mt={2}>
                      Monitoring unavailable
                    </Text>
                  )}
                </Box>
              </Flex>
              
              <Box mt={6}>
                <Heading 
                  size="sm" 
                  color={mythboundTheme.colors.secondary.white}
                  mb={4}
                >
                  User Breakdown by Role
                </Heading>
                
                <Box 
                  p={4} 
                  borderWidth="1px" 
                  borderRadius="md"
                  bg="rgba(17, 17, 22, 0.7)"
                  borderColor={mythboundTheme.colors.accent.silver}
                >
                  <Flex justifyContent="space-between" mb={2}>
                    <Text color={mythboundTheme.colors.secondary.white}>Administrators</Text>
                    <Text color={mythboundTheme.colors.primary.main}>
                      {users.filter(u => u.roles.includes(RoleType.ADMIN)).length}
                    </Text>
                  </Flex>
                  
                  <Flex justifyContent="space-between" mb={2}>
                    <Text color={mythboundTheme.colors.secondary.white}>Moderators</Text>
                    <Text color={mythboundTheme.colors.primary.main}>
                      {users.filter(u => u.roles.includes(RoleType.MODERATOR)).length}
                    </Text>
                  </Flex>
                  
                  <Flex justifyContent="space-between" mb={2}>
                    <Text color={mythboundTheme.colors.secondary.white}>Premium Users</Text>
                    <Text color={mythboundTheme.colors.primary.main}>
                      {users.filter(u => u.roles.includes(RoleType.PREMIUM)).length}
                    </Text>
                  </Flex>
                  
                  <Flex justifyContent="space-between">
                    <Text color={mythboundTheme.colors.secondary.white}>Regular Users</Text>
                    <Text color={mythboundTheme.colors.primary.main}>
                      {users.filter(u => u.roles.includes(RoleType.USER)).length}
                    </Text>
                  </Flex>
                </Box>
              </Box>
            </TabPanel>
            
            <TabPanel>
              <Heading 
                size="md" 
                color={mythboundTheme.colors.secondary.white}
                mb={4}
              >
                Audit Logs
              </Heading>
              
              <Text color={mythboundTheme.colors.secondary.white} mb={4}>
                Recent system activities and changes:
              </Text>
              
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th color={mythboundTheme.colors.accent.silver}>Timestamp</Th>
                    <Th color={mythboundTheme.colors.accent.silver}>User</Th>
                    <Th color={mythboundTheme.colors.accent.silver}>Action</Th>
                    <Th color={mythboundTheme.colors.accent.silver}>Details</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td color={mythboundTheme.colors.secondary.white}>2025-03-15 10:45:23</Td>
                    <Td color={mythboundTheme.colors.secondary.white}>admin_user</Td>
                    <Td color={mythboundTheme.colors.secondary.white}>ROLE_CHANGE</Td>
                    <Td color={mythboundTheme.colors.secondary.white}>Changed premium_user role to PREMIUM</Td>
                  </Tr>
                  <Tr>
                    <Td color={mythboundTheme.colors.secondary.white}>2025-03-14 16:30:05</Td>
                    <Td color={mythboundTheme.colors.secondary.white}>mod_user</Td>
                    <Td color={mythboundTheme.colors.secondary.white}>CONTENT_DELETE</Td>
                    <Td color={mythboundTheme.colors.secondary.white}>Removed inappropriate campaign "Test Campaign XYZ"</Td>
                  </Tr>
                  <Tr>
                    <Td color={mythboundTheme.colors.secondary.white}>2025-03-13 09:12:40</Td>
                    <Td color={mythboundTheme.colors.secondary.white}>admin_user</Td>
                    <Td color={mythboundTheme.colors.secondary.white}>SYSTEM_CONFIG</Td>
                    <Td color={mythboundTheme.colors.secondary.white}>Updated voice processing settings</Td>
                  </Tr>
                </Tbody>
              </Table>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
      
      {/* Role Change Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent bg={mythboundTheme.colors.primary.dark}>
          <ModalHeader color={mythboundTheme.colors.primary.gold}>
            Change User Role
          </ModalHeader>
          <ModalCloseButton color={mythboundTheme.colors.secondary.white} />
          <ModalBody>
            {selectedUser && (
              <>
                <Text color={mythboundTheme.colors.secondary.white} mb={4}>
                  Update role for user: <strong>{selectedUser.username}</strong>
                </Text>
                
                <FormControl>
                  <FormLabel color={mythboundTheme.colors.secondary.white}>
                    Select New Role
                  </FormLabel>
                  <Select 
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value as RoleType)}
                    bg="rgba(17, 17, 22, 0.9)"
                    color={mythboundTheme.colors.secondary.white}
                    borderColor={mythboundTheme.colors.accent.silver}
                  >
                    {Object.entries(roles).map(([roleKey, roleValue]) => (
                      <option key={roleKey} value={roleKey}>
                        {roleValue.name}: {roleValue.description}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
          </ModalBody>
          
          <ModalFooter>
            <Button
              variant="ghost"
              color={mythboundTheme.colors.secondary.white}
              mr={3}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleUserRoleChange}
            >
              Update Role
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </RoleBasedAccess>
  );
};

export default AdminDashboard;
